import React, { useState, useMemo } from "react";
import styled from "styled-components";
import InfoCircle from "./../../../assets/icons/info-circle-white.svg";
import CloseIcon from "./../../../assets/icons/x-close0.svg";
import SortDown from "./../../../assets/icons/chevron-down0.svg";
import { formatDefaultDateTime } from "../../../utils/Formatter";

//#region SCOPED CSS
const StyledLogDetailDialogBox = styled.div`
    &.input-field-card-layout-1 {
        height: 380px;
        width: 776px;
        background: #1e2631;
        border-radius: 4px;
        padding-top: 16px;
        padding-bottom: 16px;
    }
    .frame-192 {
        height: 80px;
        display: flex;
        padding-left: 16px;
        padding-right: 16px;
        width: 100%;
    }
    .frame-185 {
        display: flex;
        gap: 5px;
        width: 100%;
        flex-direction: column;
    }
    .frame-186 {
        display: flex;
        align-items: baseline;
        gap: 6px;
    }
    .frame-186-2 {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        font-size: 12px;
    }
    img.alert-circle {
        height: 12px;
        width: 12px;
    }
    img.x-close {
        height: 24px;
        width: 24px;
        cursor: pointer;
    }
    .frame-191 {
        width: 100%;
        border-bottom: 1px solid #3c485a;
    }
    .main-table {
        width: 100%;
        margin-top: 16px;
        color: #d1d1d1;
    }
    .main-table th {
        padding: 8px;
        border-bottom: 1px solid #3c485a;
    }
    .main-table .log-row {
        border-bottom: 1px solid #3c485a;
    }
    .main-table .log-row td {
        padding: 8px;
    }
    .log-component {
        width: 100%;
        padding: 0px 16px;
        height: 280px;
        overflow-y: scroll;
    }
`;
//#endregion SCOPED CSS

/**
 * @param {Object} props
 * @param {Array} props.log - Array of log objects to display.
 * @param {Function} props.onClose - Callback to close the dialog.
 * @param {string} props.logLabelBrandName - Name of the label brand.
 * @param {string} props.logRetailBrandName - Name of the retail brand.
 */
export default function LogDetailDialogBox({ log, onClose, logLabelBrandName, logRetailBrandName }) {
    // Define columns
    const columns = [
        { name: "Action", key: "action" },
        { name: "Time", key: "date" },
        { name: "By", key: "createdBy" },
        { name: "Stores", key: "maxStores" }
    ];

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: "action", direction: "asc" });

    // Sort handler
    const handleSort = key => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    // Sorted data
    const sortedLog = useMemo(() => {
        if (!Array.isArray(log)) return [];
        if (!sortConfig.key) return log;
        const sorted = [...log].sort((a, b) => {
            const aVal = a?.[sortConfig.key] ?? "";
            const bVal = b?.[sortConfig.key] ?? "";
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [log, sortConfig]);

    const getSpanWithColor = (text, color) => {
        return <span className={color}>{text}</span>;
    };

    const handleStatus = status => {
        switch (status) {
            case "Active":
                return getSpanWithColor("Connected", "text-green-500");
            case "Inactive":
                return getSpanWithColor("Disconnected", "text-red-500");
            case "Requested":
                return getSpanWithColor("Requested", "text-yellow-500");
            default:
                return status.charAt(0).toUpperCase() + status.slice(1);
        }
    };

    return (
        <StyledLogDetailDialogBox className="input-field-card-layout-1">
            <div className="frame-192">
                <div className="frame-185">
                    <div className="frame-186">
                        <div className="connection-log">CONNECTION LOG</div>
                        <img className="alert-circle" src={InfoCircle} alt="InfoCircle" />
                    </div>
                    <div className="frame-186-2">
                        <div>Label Brand: {getSpanWithColor(logLabelBrandName, "text-blue-500")}</div>
                        <div>Connected with: {getSpanWithColor(logRetailBrandName, "text-blue-500")}</div>
                    </div>
                </div>
                <img className="x-close" src={CloseIcon} alt="CloseIcon" onClick={onClose} />
            </div>
            <div className="frame-191"></div>
            <div className="log-component">
                <table className="main-table">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    style={{ cursor: "pointer", userSelect: "none", width: "25%" }}
                                    onClick={() => handleSort(col.key)}>
                                    {col.name}
                                    {sortConfig.key === col.key ? (
                                        <img
                                            src={SortDown}
                                            alt="Sort"
                                            style={{
                                                marginLeft: 4,
                                                transform: sortConfig.direction === "asc" ? "rotate(180deg)" : "none",
                                                transition: "transform 0.2s",
                                                verticalAlign: "middle",
                                                width: 20,
                                                height: 20
                                            }}
                                        />
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(sortedLog) && sortedLog.length > 0 ? (
                            sortedLog.map((row, idx) => (
                                <tr key={idx} className="log-row">
                                    <td style={{ width: "25%", textAlign: "left" }}>
                                        {handleStatus(row?.status) || "--"}
                                    </td>
                                    <td style={{ width: "25%", textAlign: "left" }}>
                                        {formatDefaultDateTime(row?.date || row?.createdAt) || "--"}
                                    </td>
                                    <td style={{ width: "25%", textAlign: "left" }}>
                                        {row?.BOstagesAccountFullName || "--"}
                                    </td>
                                    <td style={{ width: "25%", textAlign: "left" }}>
                                        {row?.maxStores ? row?.maxStores : "--"}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: "center", padding: "16px" }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </StyledLogDetailDialogBox>
    );
}
